import React, { useEffect, useState } from 'react';
import { Container, Row, Button } from 'reactstrap';
import { connect } from 'react-redux';
import { Field, reduxForm, getFormValues, change } from 'redux-form';
import { useLocation, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import TimetableIcon from 'mdi-react/TimetableIcon';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import { toast } from 'react-toastify';
import renderField from '../../../shared/components/custom/Field';
import renderTextAreaField from '../../../shared/components/custom/TextArea';
import t, { partial } from '../../../util/translation/translation';
import OfferTypes from '../types';
import Loading from '../../../shared/components/custom/Loading';
import Api from '../../../util/api';
import useModal from '../../../shared/hooks/useModal';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import validateOfferContact from './validateOfferContact';
import RenderSelectField from '../../../shared/components/form/Select';
import PreviewMessages from '../../messages/components/PreviewsMessage';
import FormCard from '../../../shared/components/custom/FormCard';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';

const OfferContactForm = ({
  onSubmit,
  offer,
  state,
  setScheduleMessageID,
  scheduleMessageID,
  scheduleMessagesForEvent,
  getScheduleMessagesByServiceId,
  tiers,
  startDate,
  setStartDate,
  totalAmountOfScheduledMessages,
  initialize,
  messageStatus,
  setMessageStatus,
  offerId,
  formValues,
  ...rest
}) => {
  const p = partial('EventsContactForm');
  const m = partial('MessagesForm');

  const { state: locationState } = useLocation();

  const [loading, setLoading] = useState(false);
  const [handleModal, visible, deleteId] = useModal();
  const [notificationLabels, setNotificationLabels] = useState([]);

  const initializeEdit = (message) => {
    setScheduleMessageID(message.id);
    const messageDate = message.date ? new Date(message.date) : null;
    setStartDate(messageDate);
    initialize({
      title:
        message?.payload?.placeholders?.TITLE ||
        message?.payload?.placeholders?.SUBJECT,
      message: message?.payload?.placeholders?.BODY,
      type: message?.payload?.channels[0]?.channel,
      scheduleTime: messageDate,
      timeToSend: 'SCHEDULED',
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const removeMessage = async (messageId) => {
    setLoading(true);
    try {
      await Api.offers.deleteScheduledMessage(offerId, messageId);
      handleModal();
      getScheduleMessagesByServiceId();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || 'messageDeletingERROR');
    }
  };

  const clearFields = () => {
    setScheduleMessageID(null);
    setStartDate('');
    initialize({
      title: '',
      message: '',
      type: 'push',
      scheduleTime: null,
      timeToSend: 'INSTANT',
    });
  };

  useEffect(() => {
    const fetchNotificationLabels = async () => {
      const res = await Api.userDevices.getAllNotificationLabels();
      setNotificationLabels(
        res.map((label) => ({
          value: label.id,
          label: label.title,
        }))
      );
    };
    fetchNotificationLabels();
  }, []);

  // Defaults that the old radio buttons set on mount via defaultChecked
  useEffect(() => {
    initialize({ type: 'push', timeToSend: 'INSTANT' });
  }, []);

  useEffect(() => {
    setScheduleMessageID(null);
  }, [offerId, messageStatus]);

  useEffect(() => {
    if (locationState?.messageId && scheduleMessagesForEvent.length > 0) {
      const messageToShow = scheduleMessagesForEvent.find(
        (msg) => msg.id === locationState?.messageId
      );
      initializeEdit(messageToShow);
    }
  }, [scheduleMessagesForEvent]);

  const RenderSubmitButtons = () => {
    let buttonName = '';
    if (scheduleMessageID) {
      buttonName = t('ScheduledMessagesPage.updateMessage');
    } else if (formValues?.timeToSend === 'SCHEDULED') {
      buttonName = t('ScheduledMessagesPage.scheduleMessage');
    } else {
      buttonName = p('sendMessage');
    }

    return (
      <>
        <div className="messages-form__footer">
          <div className="messages-form__secondary">
            {scheduleMessageID && (
              <Button
                type="button"
                size="sm"
                color="secondary"
                className="mb-0"
                onClick={clearFields}
              >
                {t('shared.clear')}
              </Button>
            )}
          </div>
          <Button type="submit" color="primary" className="mb-0">
            {buttonName}
          </Button>
        </div>
        <Link
          className="py-3"
          style={{ textDecoration: 'underline' }}
          to="/messages?tab=2"
        >
          {t('MessagesForm.scheduledAndHistory')}
        </Link>
      </>
    );
  };

  const filterPassedTime = (time) => {
    const isSameDay = moment().diff(moment(time), 'days') === 0;
    if (isSameDay) {
      return moment(time).isSameOrAfter(moment());
    }
    return true;
  };

  const isSms = formValues?.type === 'sms';

  return (
    <form
      onSubmit={(e) => onSubmit(e, state, rest.change)}
      className="form d-flex messages-form"
    >
      <Loading loading={loading} />
      <Container className="padding-0">
        <DeleteModal
          visible={visible}
          handleModal={handleModal}
          type="message"
          modalAction={() => removeMessage(deleteId)}
        />
        <Row>
          <div className="col-12 col-xl-6">
            <Field
              name="type"
              component={ChoiceCards}
              ariaLabel={t('shared.type')}
              options={[
                {
                  value: 'push',
                  icon: BellRingOutlineIcon,
                  label: 'Push',
                  hint: m('pushMessageHint'),
                },
                {
                  value: 'sms',
                  icon: MessageTextOutlineIcon,
                  label: 'SMS',
                  hint: m('smsHint'),
                },
                {
                  value: 'email',
                  icon: EmailOutlineIcon,
                  label: 'Email',
                  hint: m('emailHint'),
                },
              ]}
            />

            <FormCard title={t('shared.content')} className="mb-3">
              <div className="form-grid">
                {/* An SMS has no title, so the field is hidden for SMS */}
                {!isSms && (
                  <div className="form__form-group form-grid__full">
                    <span className="form-header">{t('shared.title')}</span>
                    <Field
                      name="title"
                      placeholder={`${t('shared.title')}`}
                      component={renderField}
                      emoji
                    />
                  </div>
                )}
                <div className="form__form-group form-grid__full">
                  <span className="form-header">{t('shared.message')}</span>
                  <Field
                    name="message"
                    placeholder={p('messageContent')}
                    component={renderTextAreaField}
                    emoji
                  />
                </div>
              </div>
            </FormCard>

            <FormCard title={t('OfferCard.other')} className="mb-3">
              <div className="form-grid">
                <div className="form__form-group">
                  <span className="form-header">
                    {t('shared.notificationLabel')}
                  </span>
                  <Field
                    name="notificationLabel"
                    component={RenderSelectField}
                    placeholder={t('MessagesForm.chooseNotificationLabel')}
                    options={notificationLabels}
                  />
                </div>
              </div>
            </FormCard>

            <FormCard
              title={t('ScheduledMessagesPage.timeToSend')}
              className="mb-3"
            >
              <Field
                name="timeToSend"
                component={ChoiceCards}
                ariaLabel={t('ScheduledMessagesPage.timeToSend')}
                options={[
                  { value: 'INSTANT', label: t('shared.instant') },
                  { value: 'SCHEDULED', label: t('shared.scheduled') },
                ]}
              />
              {formValues?.timeToSend === 'SCHEDULED' && (
                <div className="form-grid">
                  <div className="form__form-group" style={{ zIndex: 100 }}>
                    <span className="form-header">
                      {t('ScheduledMessagesPage.scheduleTime')}
                    </span>
                    <div className="form__form-group-field">
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          rest.change('scheduleTime', date);
                        }}
                        locale="pt-BR"
                        showTimeSelect
                        timeFormat="p"
                        timeIntervals={15}
                        dateFormat="Pp"
                        minDate={new Date()}
                        filterTime={filterPassedTime}
                        placeholderText="DD / MM / YYYY  -- : --"
                        disabledKeyboardNavigation
                      />
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </FormCard>
            {offer?.targetAudience === OfferTypes.TargetAudience.partner &&
              offer?.targetAudience === OfferTypes.TargetAudience.both && <></>}

            <RenderSubmitButtons />
          </div>
          <div className="col-12 col-xl-6 mb-4">
            <FormCard title={t('NewsForm.appPreview')} className="mb-3">
              <PreviewMessages
                formValues={{
                  heading: formValues?.title,
                  message: formValues?.message,
                  media: formValues?.type,
                }}
              />
            </FormCard>
            <div className="d-flex align-items-center border p-3">
              <div className="modal-icon mr-4">
                <AlertCircleOutlineIcon size="38" />
              </div>
              <div className="d-flex flex-column">
                <p>{p('pushMessageGroups')}</p>
                <p>{m('AppStorePushGuidelines')}</p>
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </form>
  );
};

const mapStateToProps = (state) => ({
  formValues: getFormValues('offers_contact_form')(state),
  state,
});

export default reduxForm({
  form: 'offers_contact_form',
  validate: validateOfferContact,
})(connect(mapStateToProps, { change })(OfferContactForm));
